import axios from 'axios';
import apiClient from './apiClient';
import { API_URL } from '../config';

// ── Error normalization ──────────────────────────────────────────────

export interface TemplateFieldError {
  field: string;
  message: string;
}

export interface NormalizedTemplateError {
  message: string;
  fieldErrors: TemplateFieldError[];
  status: number;
}

/**
 * Custom error class for template API failures.
 * Always carries a human-readable `message` string (safe for JSX rendering).
 */
export class TemplateApiError extends Error {
  public readonly status: number;
  public readonly fieldErrors: TemplateFieldError[];

  constructor(normalized: NormalizedTemplateError) {
    super(normalized.message);
    this.name = 'TemplateApiError';
    this.status = normalized.status;
    this.fieldErrors = normalized.fieldErrors;
  }
}

/**
 * Extract a human-readable error message from an Axios error response.
 *
 * FastAPI 422 responses have shape: { detail: [{type, loc, msg, input, ctx}] }
 * FastAPI HTTPException responses have shape: { detail: "string" }
 * This function handles both safely and NEVER returns a raw object.
 */
export function extractErrorMessage(err: unknown, fallback: string): string {
  if (!axios.isAxiosError(err)) {
    if (err instanceof Error) return err.message;
    if (typeof err === 'string') return err;
    return fallback;
  }

  const status = err.response?.status;
  const data = err.response?.data;

  if (!data) {
    return err.message || fallback;
  }

  const detail = data.detail;

  // FastAPI HTTPException: detail is a string
  if (typeof detail === 'string') {
    return detail;
  }

  // FastAPI 422 Pydantic validation: detail is an array of error objects
  if (Array.isArray(detail) && detail.length > 0) {
    const messages = detail.map((item: Record<string, unknown>) => {
      const loc = Array.isArray(item.loc) ? item.loc.join(' → ') : '';
      const msg = typeof item.msg === 'string' ? item.msg : JSON.stringify(item);
      return loc ? `${loc}: ${msg}` : msg;
    });
    return `Validation error: ${messages.join('; ')}`;
  }

  // Unknown shape — stringify safely
  if (typeof detail === 'object' && detail !== null) {
    return JSON.stringify(detail);
  }

  return `HTTP ${status || 'unknown'}: ${fallback}`;
}

/**
 * Build a NormalizedTemplateError from an Axios error.
 */
function normalizeAxiosError(err: unknown, fallback: string): NormalizedTemplateError {
  const message = extractErrorMessage(err, fallback);
  const fieldErrors: TemplateFieldError[] = [];
  let status = 0;

  if (axios.isAxiosError(err)) {
    status = err.response?.status || 0;
    const detail = err.response?.data?.detail;
    if (Array.isArray(detail)) {
      for (const item of detail) {
        fieldErrors.push({
          field: Array.isArray(item.loc) ? item.loc.join('.') : 'unknown',
          message: typeof item.msg === 'string' ? item.msg : JSON.stringify(item),
        });
      }
    }
  }

  return { message, fieldErrors, status };
}

// ── Payload validation ───────────────────────────────────────────────

export interface PayloadValidationResult {
  valid: boolean;
  errors: string[];
}

/**
 * Validate template content payload before sending to the API.
 * Returns structured validation result. Does NOT throw.
 */
export function validateTemplatePayload(contentJson: string): PayloadValidationResult {
  const errors: string[] = [];

  let parsed: Record<string, unknown>;
  try {
    parsed = JSON.parse(contentJson);
  } catch {
    return { valid: false, errors: ['template_content is not valid JSON'] };
  }

  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return { valid: false, errors: ['template_content must be a JSON object'] };
  }

  // sections is required
  const sections = parsed.sections;
  if (!sections || !Array.isArray(sections) || sections.length === 0) {
    errors.push('sections must be a non-empty array');
  } else {
    const names = new Set<string>();
    for (let i = 0; i < sections.length; i++) {
      const sec = sections[i];
      if (typeof sec === 'string') {
        if (!sec.trim()) errors.push(`sections[${i}] is an empty string`);
        else if (names.has(sec.toLowerCase())) {
          errors.push(`Duplicate section name: "${sec}"`);
        } else {
          names.add(sec.toLowerCase());
        }
      } else {
        errors.push(`sections[${i}] must be a string`);
      }
    }
  }

  return { valid: errors.length === 0, errors };
}

// ── Types ─────────────────────────────────────────────────────────────

export interface ReportTemplate {
  id: number;
  template_name: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  // CPT fields
  cpt_code: string | null;
  exam_name: string | null;
  modality: string | null;
  body_region: string | null;
  structure_definition: string | null;
  custom_prompt: string | null;
  // Prompt customization
  prompt_full_report: string | null;
  prompt_version: number;
  is_custom_prompt: boolean;
  is_factory_default: boolean;
  // Version tracking
  current_version: number | null;
}

export interface TemplateCreatePayload {
  template_name: string;
  template_content: string;
  created_by?: string;
  cpt_code?: string;
  exam_name?: string;
  modality?: string;
  body_region?: string;
  structure_definition?: string;
  custom_prompt?: string;
}

export interface TemplateUpdatePayload {
  template_name?: string;
  is_active?: boolean;
  cpt_code?: string;
  exam_name?: string;
  modality?: string;
  body_region?: string;
  structure_definition?: string;
  custom_prompt?: string;
}

export interface PromptUpdatePayload {
  prompt_full_report?: string | null;
  is_custom_prompt: boolean;
  updated_by?: string;
}

// ── API calls ─────────────────────────────────────────────────────────

const listTemplates = async (): Promise<ReportTemplate[]> => {
  const response = await apiClient.get(`${API_URL}/admin/templates`);
  return response.data;
};

const createTemplate = async (payload: TemplateCreatePayload): Promise<ReportTemplate> => {
  // Pre-flight validation
  const validation = validateTemplatePayload(payload.template_content);
  if (!validation.valid) {
    console.error('[templateService] createTemplate pre-flight validation failed:', validation.errors);
    throw new TemplateApiError({
      message: `Validation failed: ${validation.errors.join('; ')}`,
      fieldErrors: validation.errors.map((e) => ({ field: 'template_content', message: e })),
      status: 0,
    });
  }

  console.debug('[templateService] createTemplate payload:', {
    template_name: payload.template_name,
    cpt_code: payload.cpt_code,
    has_content: !!payload.template_content,
    content_length: payload.template_content?.length,
  });

  try {
    const response = await apiClient.post(`${API_URL}/admin/templates`, payload);
    return response.data;
  } catch (err) {
    console.error('[templateService] createTemplate failed:', err);
    throw new TemplateApiError(normalizeAxiosError(err, 'Failed to create template'));
  }
};

const updateTemplate = async (id: number, payload: TemplateUpdatePayload): Promise<ReportTemplate> => {
  try {
    const response = await apiClient.put(`${API_URL}/admin/templates/${id}`, payload);
    return response.data;
  } catch (err) {
    console.error('[templateService] updateTemplate failed:', err);
    throw new TemplateApiError(normalizeAxiosError(err, 'Failed to update template'));
  }
};

const updateTemplatePrompts = async (templateId: number, payload: PromptUpdatePayload): Promise<ReportTemplate> => {
  console.debug('[templateService] updateTemplatePrompts:', { templateId, is_custom: payload.is_custom_prompt });
  try {
    const response = await apiClient.put(`${API_URL}/admin/templates/${templateId}/prompts`, payload);
    return response.data;
  } catch (err) {
    console.error('[templateService] updateTemplatePrompts failed:', err);
    throw new TemplateApiError(normalizeAxiosError(err, 'Failed to update template prompts'));
  }
};

const templateService = {
  listTemplates,
  createTemplate,
  updateTemplate,
  updateTemplatePrompts,
};

export default templateService;

// Re-export utilities for direct use
export { validateTemplatePayload as validatePayload };
